import { BOARD } from "@grundbesitz/shared";
import type { Field } from "@grundbesitz/shared";

export type GlyphKind =
  | "railroad"
  | "electricity"
  | "water"
  | "tax"
  | "chance"
  | "community"
  | "go"
  | "jail"
  | "parking"
  | "gotojail";

/** The symbol for a board field, null for streets. */
export function glyphFor(field: Field): GlyphKind | null {
  switch (field.kind) {
    case "RAILROAD":
      return "railroad";
    case "UTILITY":
      return /wasser/i.test(field.name) ? "water" : "electricity";
    case "TAX":
      return "tax";
    case "CHANCE":
      return "chance";
    case "COMMUNITY":
      return "community";
    case "GO":
      return "go";
    case "JAIL":
      return "jail";
    case "FREE_PARKING":
      return "parking";
    case "GO_TO_JAIL":
      return "gotojail";
    case "STREET":
      return null;
  }
}

export function glyphForIndex(index: number): GlyphKind | null {
  const f = BOARD[index];
  return f ? glyphFor(f) : null;
}

const ink = { stroke: "var(--ink)" } as const;
const paper = { fill: "var(--surface)" } as const;

function Shapes({ kind }: { kind: GlyphKind }) {
  switch (kind) {
    case "railroad":
      return (
        <>
          <path d="M13 44 18 35M35 44 30 35M9 41h30" fill="none" />
          <rect x="11" y="6" width="26" height="29" rx="7" style={{ fill: "var(--accent-soft)" }} />
          <rect x="15" y="11" width="18" height="10" rx="3" style={paper} />
          <path d="M24 11v10" fill="none" strokeWidth="1.6" />
          <circle cx="17.5" cy="28" r="2.6" style={{ fill: "var(--ink)" }} stroke="none" />
          <circle cx="30.5" cy="28" r="2.6" style={{ fill: "var(--ink)" }} stroke="none" />
        </>
      );
    case "electricity":
      return <path d="M28 3 10 27h11l-3 18 20-26H26Z" fill="#f4c430" />;
    case "water":
      return (
        <>
          <path d="M24 4Q38 20 38 30a14 14 0 0 1-28 0Q10 20 24 4Z" fill="#62b8ea" />
          <path d="M17 31a7 7 0 0 0 5 6.5" fill="none" stroke="#fff" strokeOpacity="0.7" strokeWidth="2.4" />
        </>
      );
    case "tax":
      return (
        <>
          <path d="M9 36v5q0 5 15 5t15-5v-5" fill="#e8b23d" />
          <path d="M9 27v5q0 5 15 5t15-5v-5" fill="#e8b23d" />
          <path d="M9 18v5q0 5 15 5t15-5v-5" fill="#e8b23d" />
          <ellipse cx="24" cy="16" rx="15" ry="6" fill="#f4cd6a" />
          <path d="M18 16h12" fill="none" strokeWidth="1.8" />
        </>
      );
    case "chance":
      return (
        <g transform="rotate(-8 24 24)">
          <rect x="10" y="5" width="28" height="38" rx="4.5" style={{ fill: "var(--ink)" }} />
          <text
            x="24"
            y="34"
            textAnchor="middle"
            fontSize="28"
            fontWeight="700"
            stroke="none"
            style={{ fill: "var(--surface)", fontFamily: "var(--font-display)" }}
          >
            ?
          </text>
        </g>
      );
    case "community":
      return (
        <g transform="rotate(-8 24 24)">
          <rect x="10" y="5" width="28" height="38" rx="4.5" style={{ fill: "var(--accent)" }} />
          <path
            d="M24 35C11 27 12 16 19 16c3 0 5 2 5 4 0-2 2-4 5-4 7 0 8 11-5 19Z"
            style={{ fill: "var(--accent-ink)" }}
            stroke="none"
          />
        </g>
      );
    case "go":
      return <path d="M44 24H8M21 10 7 24l14 14" fill="none" strokeWidth="6" style={{ stroke: "var(--accent)" }} />;
    case "jail":
      return (
        <>
          <rect x="7" y="6" width="34" height="36" rx="4" style={paper} />
          <path d="M15 6v36M24 6v36M33 6v36" fill="none" strokeWidth="3" />
        </>
      );
    case "parking":
      return (
        <>
          <rect x="9" y="22" width="4" height="10" rx="1.5" fill="#8b6b45" />
          <circle cx="11" cy="15" r="9" fill="#3aa561" />
          <rect x="22" y="22" width="22" height="5" rx="1.5" fill="#c98b4b" />
          <rect x="22" y="30" width="22" height="5" rx="1.5" fill="#c98b4b" />
          <path d="M25 35v8M41 35v8M22 27v3M44 27v3" fill="none" />
        </>
      );
    case "gotojail":
      return (
        <>
          <circle cx="14" cy="32" r="9" fill="none" strokeWidth="4.5" />
          <circle cx="34" cy="32" r="9" fill="none" strokeWidth="4.5" />
          <path d="M23 32h2" fill="none" strokeWidth="4.5" />
          <path d="M40 6 28 18M28 8v10h10" fill="none" strokeWidth="4" style={{ stroke: "var(--danger)" }} />
        </>
      );
  }
}

/** Own line symbols for the non-street fields. Decorative; the field name always appears as text next to it. */
export function FieldGlyph({ kind, size = 32, x, y }: { kind: GlyphKind; size?: number | string; x?: number; y?: number }) {
  return (
    <svg
      x={x}
      y={y}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      aria-hidden="true"
      focusable="false"
      style={{ overflow: "visible" }}
    >
      <g {...ink} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round">
        <Shapes kind={kind} />
      </g>
    </svg>
  );
}
